import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { FaExternalLinkAlt } from 'react-icons/fa'

import api from 'services/api'
import * as S from './style'

type RadarItem = {
  id: string
  type: 'offer' | 'partner' | 'content'
  category?: string | null
  title: string
  slug: string
  subtitle?: string | null
  excerpt?: string | null
  content?: string | null
  coverImage?: string | null
  partnerName?: string | null
  externalUrl?: string | null
  ctaLabel?: string | null
  price?: number | null
  oldPrice?: number | null
  currency?: string
  metaLine?: string | null
  publishedAt?: string | null
  readingMinutes?: number | null
  seoTitle?: string | null
  seoDescription?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}

const FALLBACK = '/images/itinerary-banner.png'

const externalHref = (value?: string | null) => {
  const url = String(value || '').trim()
  if (!url) return ''

  if (/^https?:\/\//i.test(url)) return url
  if (/^\/\//.test(url)) return `https:${url}`

  return `https://${url.replace(/^\/+/, '')}`
}

const money = (value?: number | null, currency = 'BRL') =>
  value == null
    ? ''
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0
      }).format(value)

const absoluteUrl = (value?: string | null) => {
  const url = String(value || '').trim()
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  return `https://redsterna.com.br/${url.replace(/^\/+/, '')}`
}

const shouldNoIndexHost = () => {
  const hostname = window.location.hostname.toLowerCase()
  return hostname.endsWith('.up.railway.app') || hostname.includes('homolog')
}

const setSeoMeta = (
  selector: string,
  attributes: Record<string, string>,
  content: string
) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null
  if (!element) {
    element = document.createElement('meta')
    Object.entries(attributes).forEach(([key, value]) =>
      element?.setAttribute(key, value)
    )
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

const RadarShow = () => {
  const { slug } = useParams<{ slug: string }>()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['radar-post', slug],
    queryFn: async () => {
      const response = await api.get<RadarItem>('/radar/' + slug)
      return response.data
    },
    retry: false,
    refetchOnWindowFocus: false
  })

  useEffect(() => {
    if (isLoading) return

    const previousTitle = document.title
    const previousRobots =
      document.head
        .querySelector('meta[name="robots"]')
        ?.getAttribute('content') || ''

    if (isError || !data) {
      document.title = 'Conteúdo indisponível | Radar RedSterna'
      setSeoMeta('meta[name="robots"]', { name: 'robots' }, 'noindex, nofollow')

      return () => {
        document.title = previousTitle
        if (previousRobots) {
          setSeoMeta('meta[name="robots"]', { name: 'robots' }, previousRobots)
        } else {
          document.head.querySelector('meta[name="robots"]')?.remove()
        }
      }
    }

    const description =
      String(
        data.seoDescription ||
          data.excerpt ||
          data.subtitle ||
          data.content ||
          ''
      )
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 160) ||
      'Conteúdo do Radar RedSterna com informações, novidades e oportunidades para quem gosta de viajar.'
    const title = String(
      data.seoTitle || `${data.title} | Radar RedSterna`
    ).slice(0, 70)
    const canonical = `https://redsterna.com.br/radar/${encodeURIComponent(
      data.slug
    )}`
    const image =
      absoluteUrl(data.coverImage) ||
      'https://redsterna.com.br/images/thumbnail.jpg'

    document.title = title
    setSeoMeta('meta[name="description"]', { name: 'description' }, description)
    setSeoMeta(
      'meta[name="robots"]',
      { name: 'robots' },
      shouldNoIndexHost() ? 'noindex, nofollow' : 'index, follow'
    )
    setSeoMeta('meta[property="og:title"]', { property: 'og:title' }, title)
    setSeoMeta(
      'meta[property="og:description"]',
      { property: 'og:description' },
      description
    )
    setSeoMeta(
      'meta[property="og:type"]',
      { property: 'og:type' },
      data.type === 'content' ? 'article' : 'website'
    )
    setSeoMeta('meta[property="og:url"]', { property: 'og:url' }, canonical)
    setSeoMeta('meta[property="og:image"]', { property: 'og:image' }, image)
    setSeoMeta(
      'meta[name="twitter:card"]',
      { name: 'twitter:card' },
      'summary_large_image'
    )
    setSeoMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title)
    setSeoMeta(
      'meta[name="twitter:description"]',
      { name: 'twitter:description' },
      description
    )
    setSeoMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, image)

    let canonicalElement = document.head.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null
    if (!canonicalElement) {
      canonicalElement = document.createElement('link')
      canonicalElement.rel = 'canonical'
      document.head.appendChild(canonicalElement)
    }
    canonicalElement.href = canonical

    const scriptId = 'redsterna-radar-structured-data'
    document.getElementById(scriptId)?.remove()
    const script = document.createElement('script')
    script.id = scriptId
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': canonical + '#webpage',
          name: data.title,
          description,
          image,
          url: canonical,
          inLanguage: 'pt-BR',
          datePublished: data.publishedAt || data.createdAt || undefined,
          dateModified: data.updatedAt || data.publishedAt || undefined,
          isPartOf: {
            '@id': 'https://redsterna.com.br/#website'
          },
          publisher: {
            '@id': 'https://redsterna.com.br/#organization'
          },
          ...(data.type === 'content'
            ? { mainEntity: { '@id': canonical + '#article' } }
            : {})
        },
        ...(data.type === 'content'
          ? [
              {
                '@type': 'Article',
                '@id': canonical + '#article',
                headline: data.title,
                description,
                image,
                url: canonical,
                mainEntityOfPage: {
                  '@id': canonical + '#webpage'
                },
                datePublished: data.publishedAt || data.createdAt || undefined,
                dateModified: data.updatedAt || data.publishedAt || undefined,
                author: {
                  '@id': 'https://redsterna.com.br/#organization'
                },
                publisher: {
                  '@id': 'https://redsterna.com.br/#organization'
                }
              }
            ]
          : []),
        {
          '@type': 'BreadcrumbList',
          '@id': canonical + '#breadcrumb',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'RedSterna',
              item: 'https://redsterna.com.br/'
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Radar',
              item: 'https://redsterna.com.br/radar'
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: data.title,
              item: canonical
            }
          ]
        }
      ]
    })
    document.head.appendChild(script)

    return () => {
      document.getElementById(scriptId)?.remove()
      document.title = previousTitle
      if (previousRobots) {
        setSeoMeta('meta[name="robots"]', { name: 'robots' }, previousRobots)
      } else {
        document.head.querySelector('meta[name="robots"]')?.remove()
      }
    }
  }, [data, isError, isLoading])

  if (isLoading) return <S.State>Carregando conteúdo...</S.State>
  if (isError || !data) return <S.State>Conteúdo não encontrado.</S.State>

  const showPrice =
    data.type !== 'content' && data.price != null && data.price > 0

  return (
    <S.Page>
      <S.HeroImage src={data.coverImage || FALLBACK} alt="" />
      <S.Container>
        <S.Breadcrumb>
          Radar RedSterna {data.category ? '· ' + data.category : ''}
        </S.Breadcrumb>
        <h1>{data.title}</h1>
        {data.subtitle && <S.Subtitle>{data.subtitle}</S.Subtitle>}

        {(showPrice || data.externalUrl) && (
          <S.OfferBox>
            <div>
              {showPrice && (
                <S.Price>
                  {money(data.price, data.currency)}
                  {data.oldPrice != null && data.oldPrice > 0 && (
                    <del>{money(data.oldPrice, data.currency)}</del>
                  )}
                </S.Price>
              )}
              {data.metaLine && <span>{data.metaLine}</span>}
              {data.partnerName && <small>{data.partnerName}</small>}
            </div>
            {data.externalUrl && (
              <a
                href={externalHref(data.externalUrl)}
                target="_blank"
                rel="noopener noreferrer sponsored"
              >
                {data.ctaLabel || 'Ver oportunidade'} <FaExternalLinkAlt />
              </a>
            )}
          </S.OfferBox>
        )}

        {data.excerpt && <S.Intro>{data.excerpt}</S.Intro>}
        {data.content && (
          <S.Content>
            {data.content
              .split('\n')
              .map((paragraph, index) =>
                paragraph.trim() ? (
                  <p key={index}>{paragraph}</p>
                ) : (
                  <br key={index} />
                )
              )}
          </S.Content>
        )}
      </S.Container>
    </S.Page>
  )
}

export default RadarShow
